import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  Tooltip,
  Divider,
  Popconfirm,
  message,
  Badge,
} from "antd";
import {
  DeploymentUnitOutlined,
  TeamOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  BarcodeOutlined,
  EyeOutlined,
  EditOutlined,
  HistoryOutlined,
  ShopOutlined,
  AppstoreAddOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useConfig } from "../../../contexts/ConfigContext";
import { useCompany } from "../../../contexts/CompanyContext";
import { useRegister } from "../../../contexts/RegisterContext";
import { useStore } from "../../../contexts/StoreContext";
import useAuth from "../../../hooks/useAuth";
import { useItems } from "../../../hooks/useInventory";
import {
  useTraceableInstances,
  useTraceableGroups,
  useTraceableInstance,
  useTraceableGroup,
} from "../../../hooks/useTraceability";
import { TTraceableInstance, TTraceableInstanceGroup } from "../../../models/traceabilityModel";
import RegisterTraceableInstanceModal from "./RegisterTraceableInstanceModal";
import CreateTraceableLogModal from "./CreateTraceableLogModal";
import ViewTraceableInstanceDetailModal from "./ViewTraceableInstanceDetailModal";
import RegisterTraceableGroupModal from "./RegisterTraceableGroupModal";
import ViewGroupDetailModal from "./ViewGroupDetailModal";

const { Title, Text } = Typography;
const { Option } = Select;

export const ViewTraceabilityList: React.FC = () => {
  const { config } = useConfig();
  const { company } = useCompany();
  const { register } = useRegister();
  const { store } = useStore();
  const { tokenData } = useAuth.useToken("authCsrfToken", config.session, config.key);

  const [activeTab, setActiveTab] = useState<string>("instances");

  // Filters state for Instances
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined);
  const [storehouseFilter, setStorehouseFilter] = useState<number | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Filters state for Groups
  const [groupStatusFilter, setGroupStatusFilter] = useState("ALL");

  // Modals state
  const [instanceModalVisible, setInstanceModalVisible] = useState(false);
  const [editingInstance, setEditingInstance] = useState<TTraceableInstance | null>(null);

  const [logModalVisible, setLogModalVisible] = useState(false);
  const [selectedInstanceForLog, setSelectedInstanceForLog] = useState<TTraceableInstance | null>(null);

  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
  const [selectedInstanceDetailUid, setSelectedInstanceDetailUid] = useState<string | null>(null);

  const [groupModalVisible, setGroupModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<TTraceableInstanceGroup | null>(null);

  const [groupDetailVisible, setGroupDetailVisible] = useState(false);
  const [selectedGroupDetailUid, setSelectedGroupDetailUid] = useState<string | null>(null);

  const companyUid = company.current?.uid || "";
  const companyId = company.current?.id || 0;

  // Extract category and log_type options from register module_options
  const allModuleOptions = company.moduleOptions || [];
  const categoriesList = allModuleOptions.filter(
    (opt: any) => opt.type === "TRACEABLE_CATEGORY"
  );
  const logTypesList = allModuleOptions.filter(
    (opt: any) => opt.type === "TRACEABLE_LOG_TYPE"
  );

  const storehousesList = store.list || [];
  const { data: itemsData } = useItems(tokenData, {
    company: companyUid,
    module: config.module,
  });
  const storageElementsList = itemsData?.itemList || register.registerList || [];

  // Fetch Instances
  const {
    instances,
    total: totalInstances,
    isLoading: loadingInstances,
    mutate: mutateInstances,
  } = useTraceableInstances(tokenData, {
    company_id: companyId,
    status: statusFilter,
    category_option_id: categoryFilter,
    storehouse_id: storehouseFilter,
    search: search.trim() || undefined,
    page,
    limit,
  });

  // Fetch Groups
  const {
    groups,
    isLoading: loadingGroups,
    mutate: mutateGroups,
  } = useTraceableGroups(tokenData, {
    company_id: companyId,
    status: groupStatusFilter,
  });

  // Fetch Single Instance for Detail
  const { instance: currentInstanceDetail, mutate: mutateCurrentInstance } = useTraceableInstance(
    tokenData,
    {
      instance_uid: selectedInstanceDetailUid || undefined,
      company_uid: companyUid,
    }
  );

  // Fetch Single Group for Detail
  const { group: currentGroupDetail, mutate: mutateCurrentGroup } = useTraceableGroup(tokenData, {
    group_uid: selectedGroupDetailUid || undefined,
    company_uid: companyUid,
  });

  const handleOpenInstanceDetail = (inst: TTraceableInstance) => {
    setSelectedInstanceDetailUid(inst.uid);
    setDetailDrawerVisible(true);
  };

  const handleOpenGroupDetail = (grp: TTraceableInstanceGroup) => {
    setSelectedGroupDetailUid(grp.uid);
    setGroupDetailVisible(true);
  };

  const handleOpenLogModal = (inst: TTraceableInstance) => {
    setSelectedInstanceForLog(inst);
    setLogModalVisible(true);
  };

  const handleEditInstance = (inst: TTraceableInstance) => {
    setEditingInstance(inst);
    setInstanceModalVisible(true);
  };

  const handleEditGroup = (grp: TTraceableInstanceGroup) => {
    setEditingGroup(grp);
    setGroupModalVisible(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return <Tag color="green">Activo / Disponible</Tag>;
      case "IN_PRODUCTION":
        return <Tag color="blue">En Producción</Tag>;
      case "IN_USE":
        return <Tag color="cyan">En Uso</Tag>;
      case "MAINTENANCE":
        return <Tag color="orange">Mantenimiento</Tag>;
      case "SOLD":
        return <Tag color="purple">Vendido</Tag>;
      case "DISPOSED":
      case "RETIRED":
        return <Tag color="default">Retirado / Baja</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // Compute KPI metrics
  const activeCount = instances.filter((i: TTraceableInstance) => i.status === "ACTIVE" || i.status === "IN_PRODUCTION" || i.status === "IN_USE").length;
  const maintenanceCount = instances.filter((i: TTraceableInstance) => i.status === "MAINTENANCE").length;

  const instanceColumns = [
    {
      title: "Identificador",
      key: "identifier",
      render: (_: any, record: TTraceableInstance) => (
        <Space direction="vertical" size={0}>
          <a
            style={{ fontWeight: 600, color: "#1890ff", fontSize: 14 }}
            onClick={() => handleOpenInstanceDetail(record)}
          >
            {record.identifier}
          </a>
          {record.name && <Text type="secondary" style={{ fontSize: 12 }}>{record.name}</Text>}
        </Space>
      ),
    },
    {
      title: "Categoría",
      key: "category",
      render: (_: any, record: TTraceableInstance) => (
        <Tag color="geekblue">{record.module_options?.string_value || "General"}</Tag>
      ),
    },
    {
      title: "Producto Padre",
      key: "element",
      render: (_: any, record: TTraceableInstance) => {
        const el = record.storage_elements;
        return el?.name ? (
          <Text strong style={{ fontSize: 13 }}>
            {el.name}
          </Text>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: "Almacén / Ubicación",
      key: "location",
      render: (_: any, record: TTraceableInstance) => (
        <Space direction="vertical" size={0}>
          {record.storehouses?.name ? (
            <Text style={{ fontSize: 12 }}>
              <ShopOutlined /> {record.storehouses.name}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          )}
          {record.location && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.location}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Estado",
      key: "status",
      render: (_: any, record: TTraceableInstance) => getStatusBadge(record.status),
    },
    {
      title: "Grupo",
      key: "group",
      render: (_: any, record: TTraceableInstance) => {
        const activeGroup = record.traceable_instance_group_members?.find((m) => m.is_active);
        return activeGroup?.traceable_instance_groups?.name ? (
          <Tag color="purple">{activeGroup.traceable_instance_groups.name}</Tag>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TTraceableInstance) => (
        <Space size="small">
          <Tooltip title="Ver ficha y línea de tiempo">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleOpenInstanceDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Registrar evento en historial">
            <Button
              size="small"
              icon={<HistoryOutlined style={{ color: "#52c41a" }} />}
              onClick={() => handleOpenLogModal(record)}
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditInstance(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const groupColumns = [
    {
      title: "Nombre del Grupo",
      key: "name",
      render: (_: any, record: TTraceableInstanceGroup) => (
        <Space direction="vertical" size={0}>
          <a
            style={{ fontWeight: 600, color: "#722ed1", fontSize: 14 }}
            onClick={() => handleOpenGroupDetail(record)}
          >
            {record.name}
          </a>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Categoría",
      key: "category",
      render: (_: any, record: TTraceableInstanceGroup) => (
        <Tag color="geekblue">{record.module_options?.string_value || "General"}</Tag>
      ),
    },
    {
      title: "Miembros Activos",
      key: "members_count",
      render: (_: any, record: TTraceableInstanceGroup) => (
        <Badge
          count={record.active_members_count || 0}
          showZero
          style={{ backgroundColor: (record.active_members_count || 0) > 0 ? "#722ed1" : "#d9d9d9" }}
        />
      ),
    },
    {
      title: "Estado",
      key: "status",
      render: (_: any, record: TTraceableInstanceGroup) => (
        <Tag color={record.status === "ACTIVE" ? "green" : "default"}>{record.status}</Tag>
      ),
    },
    {
      title: "Fecha Creación",
      key: "created_at",
      render: (_: any, record: TTraceableInstanceGroup) =>
        dayjs(record.created_at).format("DD/MM/YYYY"),
    },
    {
      title: "Acciones",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TTraceableInstanceGroup) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleOpenGroupDetail(record)}
          >
            Ver Miembros
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditGroup(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px", maxWidth: "1300px", margin: "0 auto" }}>
      {/* Top Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Trazabilidad y Elementos Individuales
          </Title>
          <Text type="secondary">
            Gestión individual de activos, hatos/rebaños, maquinaria y lotes con historial cronológico
          </Text>
        </div>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingInstance(null);
              setInstanceModalVisible(true);
            }}
          >
            Nuevo Elemento
          </Button>
          <Button
            icon={<TeamOutlined />}
            onClick={() => {
              setEditingGroup(null);
              setGroupModalVisible(true);
            }}
          >
            Nuevo Grupo / Hato
          </Button>
          <Tooltip title="Actualizar datos">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                mutateInstances();
                mutateGroups();
              }}
            />
          </Tooltip>
        </Space>
      </div>

      {/* KPI Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Total Elementos Trazables"
              value={totalInstances}
              prefix={<DeploymentUnitOutlined style={{ color: "#1890ff" }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Activos / En Producción"
              value={activeCount}
              valueStyle={{ color: "#52c41a" }}
              prefix={<BarcodeOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="En Mantenimiento"
              value={maintenanceCount}
              valueStyle={{ color: "#fa8c16" }}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <Statistic
              title="Grupos / Hatos Registrados"
              value={groups.length}
              valueStyle={{ color: "#722ed1" }}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Tabs Container */}
      <Card style={{ borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "instances",
              label: (
                <span>
                  <DeploymentUnitOutlined /> Elementos Individuales ({totalInstances})
                </span>
              ),
              children: (
                <div>
                  {/* Filters Toolbar */}
                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
                    <Col xs={24} md={8}>
                      <Input
                        placeholder="Buscar por identificador, nombre o ubicación..."
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setPage(1);
                        }}
                        allowClear
                      />
                    </Col>
                    <Col xs={12} md={5}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Filtrar por Categoría"
                        allowClear
                        value={categoryFilter}
                        onChange={(val) => {
                          setCategoryFilter(val);
                          setPage(1);
                        }}
                      >
                        {categoriesList.map((cat) => (
                          <Option key={cat.id} value={cat.id}>
                            {cat.string_value}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={12} md={5}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Estado"
                        value={statusFilter}
                        onChange={(val) => {
                          setStatusFilter(val);
                          setPage(1);
                        }}
                      >
                        <Option value="ALL">Todos los Estados</Option>
                        <Option value="ACTIVE">Activo / Disponible</Option>
                        <Option value="IN_PRODUCTION">En Producción</Option>
                        <Option value="IN_USE">En Uso</Option>
                        <Option value="MAINTENANCE">Mantenimiento</Option>
                        <Option value="SOLD">Vendido</Option>
                        <Option value="RETIRED">Retirado / Baja</Option>
                      </Select>
                    </Col>
                    <Col xs={12} md={6}>
                      <Select
                        style={{ width: "100%" }}
                        placeholder="Filtrar por Bodega"
                        allowClear
                        value={storehouseFilter}
                        onChange={(val) => {
                          setStorehouseFilter(val);
                          setPage(1);
                        }}
                      >
                        {storehousesList.map((st: any) => (
                          <Option key={st.id} value={st.id}>
                            {st.name}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>

                  <Table
                    dataSource={instances}
                    columns={instanceColumns}
                    rowKey="id"
                    loading={loadingInstances}
                    pagination={{
                      current: page,
                      pageSize: limit,
                      total: totalInstances,
                      showSizeChanger: true,
                      pageSizeOptions: ["15", "25", "50", "100"],
                      onChange: (p, l) => {
                        setPage(p);
                        setLimit(l);
                      },
                    }}
                    locale={{
                      emptyText: "No se encontraron elementos trazables con los filtros seleccionados",
                    }}
                  />
                </div>
              ),
            },
            {
              key: "groups",
              label: (
                <span>
                  <TeamOutlined /> Grupos / Hatos / Lotes ({groups.length})
                </span>
              ),
              children: (
                <div>
                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    <Col xs={12} md={6}>
                      <Select
                        style={{ width: "100%" }}
                        value={groupStatusFilter}
                        onChange={setGroupStatusFilter}
                      >
                        <Option value="ALL">Todos los Estados</Option>
                        <Option value="ACTIVE">Activos</Option>
                        <Option value="CLOSED">Cerrados</Option>
                        <Option value="ARCHIVED">Archivados</Option>
                      </Select>
                    </Col>
                  </Row>

                  <Table
                    dataSource={groups}
                    columns={groupColumns}
                    rowKey="id"
                    loading={loadingGroups}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: "No hay grupos registrados" }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Modals & Drawers */}
      <RegisterTraceableInstanceModal
        visible={instanceModalVisible}
        onClose={() => setInstanceModalVisible(false)}
        onSuccess={() => {
          mutateInstances();
          mutateGroups();
        }}
        tokenData={tokenData}
        companyUid={companyUid}
        companyId={companyId}
        categories={categoriesList}
        storehouses={storehousesList}
        storageElements={storageElementsList}
        groups={groups}
        editingInstance={editingInstance}
      />

      <CreateTraceableLogModal
        visible={logModalVisible}
        onClose={() => setLogModalVisible(false)}
        onSuccess={() => {
          mutateInstances();
          if (selectedInstanceDetailUid) mutateCurrentInstance();
        }}
        tokenData={tokenData}
        companyUid={companyUid}
        instance={selectedInstanceForLog}
        logTypes={logTypesList}
      />

      <ViewTraceableInstanceDetailModal
        visible={detailDrawerVisible}
        onClose={() => {
          setDetailDrawerVisible(false);
          setSelectedInstanceDetailUid(null);
        }}
        instance={currentInstanceDetail}
        tokenData={tokenData}
        companyUid={companyUid}
        logTypes={logTypesList}
        onRefresh={() => {
          mutateCurrentInstance();
          mutateInstances();
        }}
        onEdit={(inst) => {
          setDetailDrawerVisible(false);
          handleEditInstance(inst);
        }}
      />

      <RegisterTraceableGroupModal
        visible={groupModalVisible}
        onClose={() => setGroupModalVisible(false)}
        onSuccess={() => {
          mutateGroups();
          mutateInstances();
        }}
        tokenData={tokenData}
        companyUid={companyUid}
        companyId={companyId}
        categories={categoriesList}
        availableInstances={instances}
        editingGroup={editingGroup}
      />

      <ViewGroupDetailModal
        visible={groupDetailVisible}
        onClose={() => {
          setGroupDetailVisible(false);
          setSelectedGroupDetailUid(null);
        }}
        group={currentGroupDetail}
        tokenData={tokenData}
        companyUid={companyUid}
        availableInstances={instances}
        onRefresh={() => {
          mutateCurrentGroup();
          mutateGroups();
          mutateInstances();
        }}
      />
    </div>
  );
};

export default ViewTraceabilityList;